import { resolveOverlayAnchor } from "@scroll/shared-types/overlays";
import type { TextOverlay } from "@scroll/shared-types/overlays";

/**
 * Compose a video with text overlays burned in using Canvas + MediaRecorder.
 *
 * This approach uses zero external dependencies:
 * 1. Draw each video frame onto a <canvas>
 * 2. Draw text overlays on top using Canvas 2D
 * 3. Capture the canvas as a MediaStream
 * 4. Record with MediaRecorder to produce a WebM/MP4 blob
 *
 * @param videoFile - The original video file
 * @param overlays - Text overlays to burn in
 * @param onProgress - Optional progress callback (0-100)
 * @returns A new File with overlays composited
 */
export async function composeVideo(
  videoFile: File,
  overlays: TextOverlay[],
  onProgress?: (progress: number) => void
): Promise<File> {
  if (overlays.length === 0) return videoFile;

  // Create video element to read frames from
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";

  const videoUrl = URL.createObjectURL(videoFile);

  try {
    // Wait for video metadata to load
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error("Failed to load video"));
      video.src = videoUrl;
    });

    const width = video.videoWidth;
    const height = video.videoHeight;
    const duration = video.duration;

    // Create canvas at video resolution
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d")!;

    // Set up MediaRecorder to capture canvas output
    const canvasStream = canvas.captureStream(30); // 30fps capture

    // Try to get audio from the original video
    const audioCtx = new AudioContext();
    const source = audioCtx.createMediaElementSource(video);
    const destination = audioCtx.createMediaStreamDestination();
    source.connect(destination);
    // Also connect to speakers so video.play() works (muted on video element,
    // but audio flows through AudioContext)
    source.connect(audioCtx.destination);

    // Merge audio track into canvas stream
    for (const track of destination.stream.getAudioTracks()) {
      canvasStream.addTrack(track);
    }

    // Pick the best available codec
    const mimeType = getPreferredMimeType();
    const recorder = new MediaRecorder(canvasStream, {
      mimeType,
      videoBitsPerSecond: 5_000_000, // 5 Mbps
    });

    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    // Start recording
    recorder.start(100); // collect data every 100ms

    // Play video and draw frames with overlays
    video.currentTime = 0;
    await video.play();

    // Frame rendering loop
    await new Promise<void>((resolve) => {
      const drawFrame = () => {
        if (video.ended || video.paused) {
          recorder.stop();
          resolve();
          return;
        }

        // Report progress
        if (onProgress && duration > 0) {
          onProgress(Math.round((video.currentTime / duration) * 100));
        }

        // Draw video frame
        ctx.drawImage(video, 0, 0, width, height);

        // Draw text overlays
        for (const overlay of overlays) {
          drawTextOverlay(ctx, overlay, width, height);
        }

        requestAnimationFrame(drawFrame);
      };

      video.onended = () => {
        // Draw the final frame
        ctx.drawImage(video, 0, 0, width, height);
        for (const overlay of overlays) {
          drawTextOverlay(ctx, overlay, width, height);
        }
        recorder.stop();
        resolve();
      };

      drawFrame();
    });

    // Wait for recorder to finish writing
    await new Promise<void>((resolve) => {
      if (recorder.state === "inactive") {
        resolve();
      } else {
        recorder.onstop = () => resolve();
      }
    });

    audioCtx.close();

    // Build output file
    const blob = new Blob(chunks, { type: mimeType });
    const extension = mimeType.includes("mp4") ? "mp4" : "webm";
    const fileName = videoFile.name.replace(/\.[^.]+$/, `.composed.${extension}`);

    onProgress?.(100);
    return new File([blob], fileName, { type: mimeType });
  } finally {
    URL.revokeObjectURL(videoUrl);
  }
}

/**
 * Draw a single text overlay onto the canvas.
 */
function drawTextOverlay(
  ctx: CanvasRenderingContext2D,
  overlay: TextOverlay,
  canvasWidth: number,
  canvasHeight: number
): void {
  const x = (overlay.x / 100) * canvasWidth;
  const y = (overlay.y / 100) * canvasHeight;
  // Scale font relative to a 500px-tall preview
  const fontSize = Math.round(overlay.fontSize * (canvasHeight / 500));

  ctx.save();

  // Position and rotate
  ctx.translate(x, y);
  if (overlay.rotation) {
    ctx.rotate((overlay.rotation * Math.PI) / 180);
  }

  // Set font
  const weight = overlay.bold ? "bold" : "normal";
  const style = overlay.italic ? "italic" : "normal";
  ctx.font = `${style} ${weight} ${fontSize}px ${overlay.fontFamily}, sans-serif`;
  ctx.textAlign = overlay.textAlign;
  ctx.textBaseline = "middle";
  ctx.globalAlpha = overlay.opacity;

  // Handle multiline text
  const lines = overlay.text.split("\n");
  const lineHeight = fontSize * 1.2;
  const totalHeight = lines.length * lineHeight;
  const startY = -(totalHeight / 2) + lineHeight / 2;

  // The drawing below is laid out around the origin (textBaseline middle,
  // vertically centered startY) — i.e. the "center" anchor, which is what
  // every web-created overlay uses. For a "top-left" overlay (created on
  // Expo and round-tripped into this flow), shift the origin so the box's
  // top-left corner lands at (x, y), matching the on-screen preview. Exact
  // for center-aligned text; left/right alignment keeps the same horizontal
  // approximation the centered path has always had.
  if (resolveOverlayAnchor(overlay, "center") === "top-left") {
    const blockWidth = Math.max(...lines.map((l) => ctx.measureText(l).width));
    ctx.translate(blockWidth / 2, totalHeight / 2);
  }

  // Draw background box if set
  if (overlay.backgroundColor) {
    const maxWidth = Math.max(...lines.map((l) => ctx.measureText(l).width));
    const padding = 8;
    let boxX = -padding;
    if (overlay.textAlign === "center") boxX = -(maxWidth / 2) - padding;
    else if (overlay.textAlign === "right") boxX = -maxWidth - padding;

    ctx.fillStyle = overlay.backgroundColor;
    ctx.fillRect(
      boxX,
      startY - lineHeight / 2 - padding,
      maxWidth + padding * 2,
      totalHeight + padding * 2
    );
  }

  // Draw each line with shadow + stroke + fill
  for (let i = 0; i < lines.length; i++) {
    const lineY = startY + i * lineHeight;

    // Shadow
    ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;

    // Stroke for readability on any background
    ctx.strokeStyle = "rgba(0, 0, 0, 0.3)";
    ctx.lineWidth = fontSize * 0.06;
    ctx.lineJoin = "round";
    ctx.strokeText(lines[i], 0, lineY);

    // Fill
    ctx.shadowColor = "transparent";
    ctx.fillStyle = overlay.color;
    ctx.fillText(lines[i], 0, lineY);
  }

  ctx.restore();
}

/**
 * Pick the best available MediaRecorder MIME type.
 * Prefer MP4 (Safari), then WebM VP9, then WebM VP8.
 */
export function getPreferredMimeType(): string {
  const candidates = [
    "video/mp4;codecs=avc1",
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
  ];
  for (const mime of candidates) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return "video/webm";
}
